// Search across all registers of the current site (number, ref, topic, building names, notes).
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { fmtDate } from '../lib/format';
import { REGISTERS, registerOfType, statusLabel, STATUS, type ItemType } from '../lib/registers';
import { Badge, Card, Empty, PageHeader } from '../ui/kit';

type Hit = { id: string; type: string; number: string | null; ref_no: string | null; title: string; status: string; submitted_on: string | null; buildings_raw: string[] };

export default function Search() {
  const { siteId, can } = useApp();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const types = REGISTERS.filter((r) => can(r.module)).map((r) => r.type);
  const safe = q.replace(/[%,()*\\]/g, ' ').trim();

  const hits = useRows<Hit>(
    () =>
      safe.length < 2 || !types.length
        ? Promise.resolve({ data: [] as Hit[], error: null })
        : supabase
            .from('items')
            .select('id, type, number, ref_no, title, status, submitted_on, buildings_raw')
            .eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000')
            .in('type', types)
            .is('deleted_at', null)
            .or(['number', 'ref_no', 'title', 'notes', 'remark', 'response', 'org_raw'].map((c) => `${c}.ilike.*${safe}*`).join(','))
            .order('submitted_on', { ascending: false, nullsFirst: false })
            .limit(200),
    [siteId, safe, types.join(',')],
  );

  const grouped = useMemo(() => REGISTERS.map((r) => ({ r, rows: hits.rows.filter((h) => h.type === r.type) })).filter((g) => g.rows.length), [hits.rows]);

  return (
    <>
      <PageHeader title="Search" sub={q ? `Results for “${q}”` : 'Type in the search box at the top'} />
      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setParams({ q: String(new FormData(e.currentTarget).get('q') ?? '') });
        }}
      >
        <input type="search" name="q" defaultValue={q} key={q} placeholder="Number, topic, building, notes…" aria-label="Search" style={{ minWidth: 280 }} />
      </form>
      {safe.length < 2 ? (
        <Card><Empty>Type at least 2 letters.</Empty></Card>
      ) : hits.loading ? (
        <Card><Empty>Searching…</Empty></Card>
      ) : grouped.length === 0 ? (
        <Card><Empty>Nothing found.</Empty></Card>
      ) : (
        grouped.map(({ r, rows }) => (
          <Card key={r.module} title={`${r.label} (${rows.length})`} pad={false}>
            <div className="table-wrap">
              <table className="t">
                <tbody>
                  {rows.map((h) => (
                    <tr key={h.id}>
                      <td>
                        <Link className="num" to={`/r/${registerOfType(h.type)?.module}/${h.id}`}>
                          {h.number ?? '—'}
                        </Link>
                        {h.ref_no ? <div className="muted small">{h.ref_no}</div> : null}
                      </td>
                      <td><div className="clip">{h.title}</div></td>
                      <td className="small">{h.buildings_raw.join(', ') || '—'}</td>
                      <td><Badge tone={STATUS[h.status]?.tone ?? 'neutral'}>{statusLabel(h.status, h.type as ItemType)}</Badge></td>
                      <td className="small">{fmtDate(h.submitted_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}
    </>
  );
}
